import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import Container from '@ui/container/container';
import InputWithOverlay from '@ui/input-with-overlay/input-with-overlay';
import SettingsEditPanel from '../../../../controls/settings-edit-panel';
import SettingsToggle from '../../../../controls/settings-toggle';
import validateGatingSettings from './helpers/validate-gating-settings';
import { GateTypes } from '@/types/registration.types';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  selectWorkingProjectRegistrationQuestions,
  selectWorkingProjectRegistrationSettings,
  selectWorkingProjectRegistrationSteps,
  selectWorkingProjectRequiredRegistrationQuestions,
} from '@/store/slices/project/selectors';
import { getRegistrationQuestions } from '@/store/slices/channel/selectors';
import { saveWorkingProjectByFields } from '@/store/slices/project';
import { deriveRegistrationUpdate } from '../../helpers/derive-registration-update';

type Props = {
  isSelected: boolean;
  onToggle: (on: boolean) => void;
  disabled?: boolean;
};

const PasscodeTypeOption = ({ isSelected, onToggle, disabled = false }: Props) => {
  const dispatch = useAppDispatch();
  const registrationSettings = useAppSelector(selectWorkingProjectRegistrationSettings);
  const registrationSteps = useAppSelector(selectWorkingProjectRegistrationSteps);
  const registrationQuestions = useAppSelector(selectWorkingProjectRegistrationQuestions);
  const requiredRegistrationQuestions = useAppSelector(selectWorkingProjectRequiredRegistrationQuestions);
  const channelRegistrationQuestions = useAppSelector(getRegistrationQuestions);

  const requirementsMap = registrationSettings?.requirements;
  const sourceBlockedDomains = registrationSettings?.blockedDomainEmails;
  const sourceAllowDomains = requirementsMap?.[GateTypes.domain_email]?.allowedDomains;
  const sourceDomainTab =
    registrationSettings?.domain_gate_mode ??
    ((sourceAllowDomains?.length ? 'allow' : sourceBlockedDomains?.length ? 'block' : 'allow') as 'allow' | 'block');
  const sourceSharedPasscode = requirementsMap?.[GateTypes.shared_passcode]?.sharedPasscode;

  const [workingSharedPasscode, setWorkingSharedPasscode] = useState<string>(sourceSharedPasscode || '');
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setWorkingSharedPasscode(sourceSharedPasscode || '');
  }, [sourceSharedPasscode]);

  useEffect(() => {
    if (!isSelected) {
      setDirty(false);
    }
  }, [isSelected]);

  const gateError = useMemo(() => {
    if (!isSelected) return '';
    const errors = validateGatingSettings({
      access: GateTypes.shared_passcode,
      domainTab: 'allow',
      allowDomains: [],
      blockDomains: [],
      sharedPasscode: workingSharedPasscode,
    });
    return errors?.shared_passcode || '';
  }, [isSelected, workingSharedPasscode]);

  const handleSave = useCallback(() => {
    if (!registrationSettings) return;
    const update = deriveRegistrationUpdate(
      {
        registration_settings: registrationSettings,
        registration_steps: registrationSteps,
        registration_questions: registrationQuestions,
        required_registration_questions: requiredRegistrationQuestions,
      },
      {
        kind: 'gate',
        gateType: GateTypes.shared_passcode,
        domainGateMode: sourceDomainTab,
        allowDomains: sourceAllowDomains || [],
        blockDomains: sourceBlockedDomains || [],
        sharedPasscode: workingSharedPasscode,
      },
      channelRegistrationQuestions
    );
    dispatch(saveWorkingProjectByFields({ saveId: 'gating-settings', ...update }));
  }, [
    channelRegistrationQuestions,
    dispatch,
    registrationQuestions,
    registrationSettings,
    registrationSteps,
    requiredRegistrationQuestions,
    sourceAllowDomains,
    sourceBlockedDomains,
    sourceDomainTab,
    workingSharedPasscode,
  ]);

  const handleCancel = useCallback(() => {
    setWorkingSharedPasscode('');
  }, []);

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setWorkingSharedPasscode(event.target.value.toLowerCase());
  }, []);

  const shouldShowError = useMemo(() => {
    const _error = gateError || '';
    if (_error.includes('Passcode field must have a value')) {
      return dirty ? _error : '';
    }
    return _error;
  }, [gateError, dirty]);

  return (
    <SettingsEditPanel
      isEditing={isSelected}
      onSave={handleSave}
      onCancel={handleCancel}
      cancelLabel="Reset"
      saveDisabled={!!gateError}
      warningMessage={shouldShowError}
      expandContent={
        <Container className="padding-h-16">
          <InputWithOverlay
            designSize="small"
            inputWidth="396px"
            inputProps={{
              placeholder: 'Add passcode',
              value: workingSharedPasscode,
              onChange: handleChange,
              onBlur: () => setDirty(true),
            }}
          />
        </Container>
      }
    >
      <SettingsToggle
        label="Passcode"
        value={isSelected}
        sKey={GateTypes.shared_passcode.toString()}
        onToggle={(_, on) => onToggle(on)}
        disabled={disabled}
        tooltipText="Create a universal passcode that users need to access"
      />
    </SettingsEditPanel>
  );
};

export default memo(PasscodeTypeOption);
